/**
 * 牌桌嘴炮的本机交互态。
 *
 * 不存聊天内容 —— 历史的唯一真源是 `netStore.chat`（服务端 chatTail + 增量），
 * 这里只记「抽屉开没开、正在打的字、看到哪了、刚发了什么」。换房间整体清空。
 */
import { create } from 'zustand'

import { pushStamp } from './talkDerive.js'

/** 最近发出的聊天帧 cid 留几个（认领被拒的错误用） */
const CID_KEEP = 8

export interface TalkState {
  readonly open: boolean
  /** 输入框有焦点（软键盘弹起） */
  readonly composing: boolean
  readonly draft: string
  /** 看到的最新一条聊天的服务端时间；null = 还没进过房间 */
  readonly seenAt: number | null
  /** 本机时钟：最近一次发手打的话 */
  readonly lastTextAt: number | null
  readonly phraseStamps: readonly number[]
  /** 最近发出的 chat / phrase 帧的 cid；被拒时据此认领 */
  readonly sentCids: readonly number[]
  /** 最近一次手打发出的原文：被限流拒掉时还回输入框 */
  readonly lastText: { readonly cid: number; readonly text: string } | null
  /** 抽屉里的一行提示（说太快了 / 房主关了聊天） */
  readonly notice: string | null
  /** 这一步我已经催过（handId:actionNo） */
  readonly nudgedKey: string | null
  /** 空闲条上轮播 4 秒的「老周：劳资不信」 */
  readonly ticker: { readonly id: number; readonly who: string; readonly text: string } | null

  setOpen(open: boolean): void
  setComposing(on: boolean): void
  setDraft(text: string): void
  markSeen(at: number): void
  noteText(cid: number, text: string, now: number): void
  notePhrase(cid: number, now: number): void
  isTalkCid(cid: number | undefined): boolean
  setNotice(text: string | null): void
  setNudged(key: string): void
  pushTicker(who: string, text: string): number
  clearTicker(id: number): void
  reset(): void
}

let tickerSeq = 0

const INITIAL = {
  open: false,
  composing: false,
  draft: '',
  seenAt: null,
  lastTextAt: null,
  phraseStamps: [],
  sentCids: [],
  lastText: null,
  notice: null,
  nudgedKey: null,
  ticker: null,
} as const

export const useTalkStore = create<TalkState>((set, get) => ({
  ...INITIAL,

  // 关抽屉的同时清掉提示：下次打开不该还挂着上一次的「说太快了」
  setOpen: (open) => set(open ? { open } : { open, notice: null }),
  setComposing: (composing) => set({ composing }),
  setDraft: (draft) => set({ draft }),
  markSeen: (at) => {
    const prev = get().seenAt
    if (prev === null || at > prev) set({ seenAt: at })
  },
  noteText: (cid, text, now) =>
    set((s) => ({
      lastTextAt: now,
      lastText: { cid, text },
      sentCids: [...s.sentCids, cid].slice(-CID_KEEP),
      notice: null,
    })),
  notePhrase: (cid, now) =>
    set((s) => ({
      phraseStamps: pushStamp(s.phraseStamps, now),
      sentCids: [...s.sentCids, cid].slice(-CID_KEEP),
      notice: null,
    })),
  isTalkCid: (cid) => cid !== undefined && cid > 0 && get().sentCids.includes(cid),
  setNotice: (notice) => set({ notice }),
  setNudged: (nudgedKey) => set({ nudgedKey }),
  pushTicker: (who, text) => {
    const id = ++tickerSeq
    set({ ticker: { id, who, text } })
    return id
  },
  clearTicker: (id) => {
    if (get().ticker?.id === id) set({ ticker: null })
  },
  reset: () => set({ ...INITIAL }),
}))
